
import React, { useState, useEffect } from 'react';
import { TextField, Button, Container, Box, InputLabel, FormControl, MenuItem, Select, FormHelperText, Typography } from '@mui/material';
import { createResource, getResourceById, updateResource } from '../services/resourceService';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MediaUploadField from './MediaUploadField';

function ResourceForm() {
  const [initialValues, setInitialValues] = useState({
    resourceId: null,
    title: '',
    titleAr: '',
    content: '',
    contentAr: '',
    type: '',
    typeAr: '',
    imageSrc: '',
    videoUrl: '',
  });
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchResource();
    }
  }, [id]);

  const fetchResource = async () => {
    try {
      const data = await getResourceById(id);
      setInitialValues({
        resourceId: data.resourceId,
        title: data.title,
        titleAr: data.titleAr || '',
        content: data.content,
        contentAr: data.contentAr || '',
        type: data.type,
        typeAr: data.typeAr || '',
        imageSrc: data.imageSrc || '',
        videoUrl: data.videoUrl || '',
      });
    } catch (error) {
      console.error('Error fetching resource:', error);
      alert('Failed to fetch resource data.');
    }
  };

  const ResourceSchema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    content: Yup.string().required('Content is required'),
    type: Yup.string().required('Type is required'),
    videoUrl: Yup.string()
      .url('Must be a valid URL')
      .nullable()
      .notRequired()
      .test('is-youtube', 'Must be a valid YouTube URL', function(value) {
        if (!value) return true;
        return value.includes('youtube.com') || value.includes('youtu.be');
      }),
    // Arabic fields are optional but have max length validation
    titleAr: Yup.string().max(100, 'Arabic title should not exceed 100 characters'),
  });

  const handleSubmit = async (values) => {
    try {
      if (id) {
        await updateResource(id, values);
      } else {
        await createResource(values);
      }
      navigate('/resources');
    } catch (error) {
      console.error('Error saving resource:', error);
      alert('Error saving resource.');
    }
  };

  const resourceTypes = [
    { en: 'Article', ar: 'مقال' },
    { en: 'Media', ar: 'وسائط' },
    { en: 'Guide', ar: 'دليل' },
    { en: 'Template', ar: 'نموذج' }
  ];

  return (
    <DashboardLayout>
      <Container maxWidth="md">
        <Box mt={4}>
          <Typography variant="h4" gutterBottom>
            {id ? 'Edit Resource' : 'Create Resource'}
          </Typography>
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={ResourceSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, setFieldValue }) => (
              <Form>
                {/* Title Fields */}
                <Typography variant="h6" sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
                  Title & Content
                </Typography>

                <Box display="flex" gap={2}>
                  <TextField
                    label="Title"
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    required
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                  />
                  <TextField
                    label="Arabic Title"
                    name="titleAr"
                    value={values.titleAr}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    error={touched.titleAr && Boolean(errors.titleAr)}
                    helperText={touched.titleAr && errors.titleAr}
                    inputProps={{ dir: 'rtl' }}
                  />
                </Box>

                {/* Content Fields */}
                <Box mt={2} mb={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Content (English)
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={values.content}
                    onChange={(value) => setFieldValue('content', value)}
                    modules={{
                      toolbar: [
                        [{ header: '1' }, { header: '2' }, { font: [] }],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                        [{ align: [] }],
                        [{ color: [] }, { background: [] }],
                        ['link', 'image'],
                        ['clean'],
                      ],
                    }}
                  />
                  {touched.content && errors.content && (
                    <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                      {errors.content}
                    </Typography>
                  )}
                </Box>

                <Box mt={2} mb={2}>
                  <Typography variant="subtitle1" gutterBottom>
                    Content (Arabic)
                  </Typography>
                  <ReactQuill
                    theme="snow"
                    value={values.contentAr}
                    onChange={(value) => setFieldValue('contentAr', value)}
                    modules={{
                      toolbar: [
                        [{ header: '1' }, { header: '2' }, { font: [] }],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                        [{ align: [] }],
                        [{ color: [] }, { background: [] }],
                        ['link', 'image'],
                        ['clean'],
                      ],
                    }}
                  />
                </Box>

                {/* Type Fields */}
                <Typography variant="h6" sx={{ mt: 3, mb: 2, color: 'primary.main' }}>
                  Resource Type & Media
                </Typography>

                <Box display="flex" gap={2}>
                  <FormControl
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    required
                    error={touched.type && Boolean(errors.type)}
                  >
                    <InputLabel>Type</InputLabel>
                    <Select
                      name="type"
                      value={values.type}
                      onChange={handleChange}
                      label="Type"
                    >
                      {resourceTypes.map((type) => (
                        <MenuItem key={type.en} value={type.en}>
                          {type.en}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.type && errors.type && (
                      <FormHelperText>{errors.type}</FormHelperText>
                    )}
                  </FormControl>

                  <TextField
                    label="Arabic Type"
                    name="typeAr"
                    value={values.typeAr}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    placeholder="نوع المورد بالعربية"
                    inputProps={{ dir: 'rtl' }}
                  />
                </Box>

                <MediaUploadField
                  label="Resource Image"
                  value={values.imageSrc}
                  onChange={(nextValue) => setFieldValue('imageSrc', nextValue)}
                  kind="image"
                  scope="resources"
                  slug={values.title}
                  error={errors.imageSrc}
                  touched={touched.imageSrc}
                  helperText="Optional. Upload an image or keep using an external/manual URL."
                />

                {values.type === 'Media' && (
                  <TextField
                    label="YouTube Video URL (optional)"
                    name="videoUrl"
                    value={values.videoUrl}
                    onChange={handleChange}
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    placeholder="https://www.youtube.com/watch?v=..."
                    error={touched.videoUrl && Boolean(errors.videoUrl)}
                    helperText={
                      (touched.videoUrl && errors.videoUrl) ||
                      "Add a YouTube URL to enable video playback in the resource card"
                    }
                  />
                )}

                <Box mt={4}>
                  <Button type="submit" variant="contained" color="primary" fullWidth>
                    {id ? 'Update Resource' : 'Create Resource'}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Container>
    </DashboardLayout>
  );
}

export default ResourceForm;
